import { useEffect } from 'react';
import { useStore } from 'react-redux';

import { env } from '@/env';
import { getMyLogtoSub } from '@/lib/logto';
import { impostaStatoTempoReale } from '@/lib/qsocials/statoTempoReale';
import { rileggiPerEvento, rileggiTutto } from '@/lib/qsocials/rilettura';
import { ascoltaRientro, avviaTempoReale, TAG_ALLA_SOTTOSCRIZIONE } from '@/lib/qsocials/tempoReale';
import { chiediWsToken } from '@/lib/qsocials/wsToken';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

/**
 * Il collegamento a `qsocials-ws`, montato UNA volta in `AppShell` e non per pagina: una pagina che si
 * smonta non deve chiudere il canale da cui arrivano le richieste d'amicizia delle altre.
 * La logica sta in `lib/qsocials/tempoReale.ts`; qui c'e' solo il filo con Redux e l'ambiente.
 *
 * ⚠️ Il `sub` si legge UNA volta, quando `authed` diventa vero, e l'effect non dipende da lui. Regge perche'
 * «sessione presente» implica «access token presente» (misurato su eb162bb, cantiere tma-orcb-1-qsocials):
 *  - `authed` parte da `hasStoredSession()` = access token O refresh token (`store/auth.slice.ts:60`,
 *    `lib/logto.ts:563-565`) e poi lo accende solo `useLogtoAuth` (`hooks/useLogtoAuth.ts:76,92,150`);
 *  - un refresh token NASCE solo in `persistTokens`, che scrive PRIMA l'access token
 *    (`lib/logto.ts:461-462`); gli altri scrittori (`doRedeem` `:1158`, `persist*Token` `:1227,1244,1276,
 *    1370,1414`) ruotano un refresh gia' esistente;
 *  - l'access token si cancella solo insieme al refresh (`clearStoredTokens`, `lib/logto.ts:525-528`);
 *  - `getMyLogtoSub` decodifica anche un token SCADUTO (`lib/logto.ts:1326-1331`, nessun controllo di `exp`).
 * ⇒ Chi aggiunge un percorso che accende `authed` senza un access token in `localStorage` rompe questa
 *   premessa: il tempo reale resterebbe `spento` per tutta la sessione, in silenzio.
 */
export function useTempoRealeQsocials(): void {
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const dispatch = useAppDispatch();
  const store = useStore();

  useEffect(() => {
    const chiudi = avviaTempoReale({
      url: env.qsocialsWsUrl,
      socialAcceso: env.featureSocialEnabled,
      sub: authed ? getMyLogtoSub() : null,
      chiediToken: chiediWsToken,
      onSottoscritto: () => rileggiTutto(dispatch, TAG_ALLA_SOTTOSCRIZIONE),
      onEvento: (data) => rileggiPerEvento(data, dispatch, store.getState),
      onStato: impostaStatoTempoReale,
    });
    // R7: al rientro dall'app si rilegge subito (solo col tempo reale acceso: a interruttore vuoto la
    // TMA resta com'era).
    const smetti = chiudi ? ascoltaRientro(() => rileggiTutto(dispatch, TAG_ALLA_SOTTOSCRIZIONE)) : null;
    return () => {
      smetti?.();
      chiudi?.();
    };
  }, [authed, dispatch, store]);
}
